/**
 * config/severity.js
 * Attack severity classification — universal keyword-based rules engine
 * To add new attack types: add keywords to the relevant array below.
 * No other files need to change.
 */

// Re-export colors from constants (single source of truth)
export { SEVERITY_COLORS, SEVERITY_BG } from "./constants";

/**
 * Maps any attack label to a severity tier.
 * Priority: critical > high > medium > info > low (default)
 */
export const severityFromLabel = (label) => {
  const l = (label || "").toLowerCase().trim()
    .replace(/[^\x00-\x7F]/g, " ")
    .replace(/\s+/g, " ").trim();

  if (["dos","ddos","syn_flood","syn flood","syn-flood","exploit","exploitation",
       "exploit_attempt","injection","sql","sqli","sql injection","backdoor",
       "ransomware","malware","wiper","rootkit","brute","brute_force","brute force",
       "brute-force","xss","cross site","cross-site","cross_site","web attack",
       "webattack","bot","botnet","heartbleed","eternalblue","log4shell","log4j",
       "infiltration","infilterat","infilteration","exfiltration","data_exfil","back","shellcode",
       "zero_day","zeroday","zerolog","printspooler","rce","remote code",
       "command injection","slowloris","hulk","goldeneye","rudy","slowhttp",
       "hulkdoser","flood","back attack"," back","attack",
  ].some(x => l.includes(x))) return "critical";

  if (["scan","portscan","port scan","port-scan","nmap","probe","sweeping",
       "mitm","man in the middle","arp_poisoning","arp poisoning","rdp","smb",
       "smb_probe","ftp_patator","ftp-patator","ssh_patator","ssh-patator",
       "patator","privilege","privesc","escalation","lateral","pivot","webshell",
       "web_shell","web shell","dns_tunnel","dnstunnel","tunneling","keylogger",
       "keylogging","credential dump","mimikatz","pass_the_hash","kerberoast",
  ].some(x => l.includes(x))) return "high";

  if (["recon","reconnaissance","footprint","fuzzing","fuzz","c2","c2_beacon",
       "beacon","command and control","exfil","data theft","data collection",
       "phishing","spearphishing","vishing","social engineering","pretexting",
       "supply chain","third party",
  ].some(x => l.includes(x))) return "medium";

  if (["normal","benign","legitimate","observe","background","clean","safe",
       "allowed","permit",
  ].some(x => l.includes(x))) return "info";

  return "low";
};

export const getAttackSource = (attackTypes) => {
  const labels = (attackTypes || []).map(a => a.name.toLowerCase());
  const has = (keywords) => keywords.some(k => labels.some(l => l.includes(k)));

  if (has(["modbus","ics","scada","ot ","ros","ugv","coil","register","plc","dnp3"]))
    return { primary:"CISA ICS-CERT 2024", secondary:"IBM Industrial $5.56M",   type:"ICS/OT"            };
  if (has(["ransomware","ransom","encrypt","locker","wiper"]))
    return { primary:"Verizon DBIR 2024",  secondary:"IBM 2024 $4.88M avg",      type:"Ransomware"        };
  if (has(["apt","nation","espionage","state actor","backdoor","rootkit"]))
    return { primary:"Microsoft MDDR 2024",secondary:"Mandiant M-Trends 2024",   type:"Nation-State APT"  };
  if (has(["ddos","dos","syn flood","udp flood","amplification","flood","hulk","slowloris"]))
    return { primary:"NETSCOUT TI 2024",   secondary:"IBM 2024 $4.88M avg",      type:"DDoS/DoS"          };
  if (has(["phishing","social","pretexting","bec","vishing"]))
    return { primary:"Verizon DBIR 2024",  secondary:"FBI IC3 2024",             type:"Social Engineering"};
  if (has(["web attack","xss","sql","injection","brute","sqli","csrf"]))
    return { primary:"IBM 2024",           secondary:"OWASP Top 10 2024",        type:"Web Application"   };
  if (has(["credential","password","spray","stuffing","kerberoast"]))
    return { primary:"Microsoft MDDR 2024",secondary:"Verizon DBIR 2024",        type:"Credential Attack" };
  if (has(["infiltrat","exfil","data theft"]))
    return { primary:"Verizon DBIR 2024",  secondary:"IBM 2024",                 type:"Data Exfiltration" };
  if (has(["port scan","scan","recon","nmap","enumerat"]))
    return { primary:"Verizon DBIR 2024",  secondary:"IBM 2024",                 type:"Reconnaissance"    };
  return   { primary:"IBM 2024",           secondary:"Verizon DBIR 2024",        type:"General"           };
};
